import * as ko from "knockout";
import {config, global, lm} from "../../../appContext";
import {IDropdownButton} from "../../button";
import PromiseRequest from "../../../req/PromiseRequest";
import {createErrorMessage, escapeHTML, getSyncFieldDisplayName, isBit, validateObjectGuid} from "../../../util/misc";
import {IAuthenticationType, ICompany, IMessageData, IObjectData} from "../../../index";
import {IComboboxV2Options} from "../../combobox/v2";
import {convertFromServerString, getDate, validateServerDate} from "../../../util/datetime";
import {AuthenticationTypeFlag} from "../../../constants/AuthenticationTypeFlag";
import {IdentityProviderProtocol} from "../../../constants/IdentityProviderProtocol";
import {ObjectTypeId} from "../../../constants";
import {getHash} from "../../../constants/RouteDefinitions";
import {ImgBase64_GrandId} from "../../../constants/Images";
import {applyBuiltinSelection, BuiltinRole, authProtocolKey, protocolKey, removeBuiltin} from "./auth";
import {nsCustomerGeneral} from "../general/fn";
import {registerSsoTips} from "./tips";
import {TestLoginSession} from "./testLogin";
import detailsHtml from "./details.html?raw";
import urlEncodeCustomerKeys = nsCustomerGeneral.urlEncodeCustomerKeys;

export let template: string = detailsHtml;

registerSsoTips();

interface IIdpCertificate {
    thumbprint: string
    notafter: string
    isprimary: boolean
}

interface IIdp {
    id: number
    displayname: string
    protocol: IdentityProviderProtocol
    entityid: string
    ssourl: string
    slourl: string
    nameidformat: string
    isenabled: boolean
    metadataurl: string
    claimmapping: string
    wantassertionsigned: boolean
    wantresponsesigned: boolean
    allowclockskewminutes: number
    priority?: number
    certificates: IIdpCertificate[]
    warnings?: string[]
    attributes?: IClaimAttribute[]
    createdby?: number
    createddate?: string
    modifiedby?: number | null
    modifieddate?: string
    metadatalastrefreshed?: string
    metadatanextrefreshat?: string
    metadatarefreshfailurecount?: number
}

interface IClaimAttribute {
    name: string
    friendlyname: string
}

interface IClaimSuggestion {
    name: string
    label: string
}

interface IMetadataPreview {
    entityid: string
    ssourl: string
    slourl: string
    nameidformat: string
    metadatavaliduntil: string
    warnings: string[]
    certificates: IIdpCertificate[]
    attributes?: IClaimAttribute[]
}

interface IFieldOption {
    id: string
    labelKey: string
}

interface IWizardStep {
    labelKey: string
    subtitleKey?: string
    subtitleText?: string
}

interface ISsoDetailsParams {
    onAuthenticationSaved?: (company: ICompany) => void
    draft?: boolean
    draftRevision?: KnockoutObservable<number>
    readDraftCompany?: () => ICompany
    writeDraftCompany?: (company: ICompany) => void
}

const CREDENTIAL_FLAGS: number[] = [
    AuthenticationTypeFlag.AAD_FEDERATION,
    AuthenticationTypeFlag.GOOGLE,
    AuthenticationTypeFlag.GRAND_ID_BANKID_SE,
];

const BUILTIN_ORDER: number[] = [
    AuthenticationTypeFlag.ID_PORTEN,
    AuthenticationTypeFlag.AAD_FEDERATION,
    AuthenticationTypeFlag.GOOGLE,
    AuthenticationTypeFlag.GRAND_ID_BANKID_SE,
    AuthenticationTypeFlag.TELIA_FINLAND,
    AuthenticationTypeFlag.EADM_NATIVE,
    AuthenticationTypeFlag.EADM_NATIVE_SMS,
];

interface IProviderRow {
    key: string
    kind: "builtin" | "saml"
    name: string
    subtitle: string
    protocolKey: string
    active: boolean
    roleKey: string
    statusClass: string
    flag?: number
    saml?: IIdp
}

const IDENTIFIER_FIELDS: IFieldOption[] = [
    {id: "upn", labelKey: "sso.companyidp.field.upn"},
    {id: "email", labelKey: "sso.companyidp.field.email"},
    {id: "nationalId", labelKey: "sso.companyidp.field.nationalid"},
];

export class viewModel {
    readonly identifierFields = IDENTIFIER_FIELDS;

    readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    readonly providers: KnockoutObservableArray<IIdp> = ko.observableArray<IIdp>([]);
    readonly isLoading = ko.observable(false);
    readonly spMetadataLoading = ko.observable(false);
    readonly spRollover = ko.observable(false);
    readonly showSpRollover = ko.pureComputed((): boolean => this.spRollover() && !this.spMetadataLoading());
    readonly mode = ko.observable<"list" | "wizard">("list");
    readonly step = ko.observable(1);
    readonly editingId: KnockoutObservable<number | null> = ko.observable(null);
    private readonly editingProvider: KnockoutObservable<IIdp | null> = ko.observable(null);
    private readonly actorNames = ko.observable<Record<number, string>>({});

    readonly displayName = ko.observable("");
    readonly metadataUrl = ko.observable("");
    readonly metadataSource = ko.observable<"file" | "url">("url");
    readonly uploadedXml = ko.observable("");
    readonly uploadedFileName = ko.observable("");
    readonly preview: KnockoutObservable<IMetadataPreview | null> = ko.observable(null);
    readonly previewError = ko.observable("");
    readonly isPreviewing = ko.observable(false);
    readonly isDragging = ko.observable(false);

    readonly primaryField = ko.observable<string | undefined>(undefined);
    readonly fieldPlaceholder = ko.pureComputed((): string => lm.t("sso.companyidp.field.select") ?? "Select field...");
    readonly primarySource = ko.observable("");
    readonly claimSuggestions = ko.pureComputed((): IClaimSuggestion[] =>
        (this.preview()?.attributes ?? [])
            .filter(attribute => !!attribute?.name?.trim())
            .map(attribute => ({
                name: attribute.name.trim(),
                label: attribute.friendlyname?.trim() || attribute.name.trim(),
            })),
    );
    readonly claimComboboxOptions: IComboboxV2Options<IClaimSuggestion> = {
        data: this.claimSuggestions,
        input: this.primarySource,
        option: {
            label: claim => claim.label,
            tooltip: claim => claim.name,
            key: claim => claim.name,
            match: (claim, query) =>
                claim.label.toLowerCase().includes(query)
                || claim.name.toLowerCase().includes(query),
        },
        inputAfterSelect: claim => claim.name,
        inputAttrs: {
            placeholder: () => lm.t("sso.companyidp.claim.placeholder"),
            autocomplete: "off",
            onePasswordIgnore: true,
        },
    };

    readonly wantAssertionSigned = ko.observable(true);
    readonly wantResponseSigned = ko.observable(false);
    readonly clockSkew = ko.observable("5");
    readonly isSaving = ko.observable(false);
    readonly testingIdpId = ko.observable<number | null>(null);
    readonly expandedRowKey = ko.observable<string | null>(null);
    readonly detailTab = ko.observable<"connection" | "attributes">("connection");
    readonly role = ko.observable<BuiltinRole>("secondary");
    readonly company: KnockoutObservable<ICompany | null> = ko.observable(null);
    readonly catalog: KnockoutObservableArray<IAuthenticationType> = ko.observableArray<IAuthenticationType>([]);
    readonly builtinFlag = ko.observable(0);
    readonly builtinRole = ko.observable<BuiltinRole>("secondary");
    readonly builtinTenant = ko.observable("");
    readonly builtinGoogle = ko.observable("");
    readonly builtinApiKey = ko.observable("");
    readonly builtinServiceKey = ko.observable("");
    readonly builtinError = ko.observable("");
    readonly selectedBuiltinFlag: KnockoutObservable<number | null> = ko.observable(null);
    readonly samlSelected = ko.observable(false);
    readonly builtinIsNew = ko.observable(false);
    readonly builtinStep = ko.observable<"credentials" | "review" | null>(null);

    private readonly onAuthenticationSaved: ((company: ICompany) => void) | undefined;
    private readonly readDraftCompany: (() => ICompany) | undefined;
    private readonly writeDraftCompany: ((company: ICompany) => void) | undefined;
    private readonly draftRevisionSub: KnockoutSubscription | undefined;
    readonly draft: boolean;
    readonly allowCustom: boolean;
    readonly showList: boolean;
    private readonly orgnr: string;
    private editingEnabled = true;
    private readonly reqCompany = new PromiseRequest("getcompany");
    private readonly reqTypes = new PromiseRequest("getauthenticationtypes");
    private readonly reqUpdateCompany = new PromiseRequest("updatecompany");
    private readonly reqList = new PromiseRequest("getcompanyidps");
    private readonly reqPreview = new PromiseRequest("previewcompanyidpmetadata");
    private readonly reqCreate = new PromiseRequest("createcompanyidp");
    private readonly reqUpdate = new PromiseRequest("updatecompanyidp");
    private readonly reqDelete = new PromiseRequest("deletecompanyidp");
    private readonly reqTest = new PromiseRequest("testcompanyidplogin");
    private readonly testLoginSession = new TestLoginSession(dialogKey => {
        this.testingIdpId(null);
        if (dialogKey) {
            this.showTestResult(dialogKey);
        }
    });
    private spMetadataGeneration = 0;
    private spMetadataLoad: { generation: number, request: Promise<{ xml: string, rollover: boolean }> } | null = null;
    private spMetadataResult: { generation: number, metadata: { xml: string, rollover: boolean } } | null = null;

    readonly spMetadataUrl = ko.pureComputed((): string => {
        if (!this.orgnr) {
            return "";
        }
        const hub = config.getUrlIdentityHub().replace(/\/$/, "");
        return `${hub}/saml/metadata/${encodeURIComponent(this.orgnr)}`;
    });

    readonly canLeaveConnection = ko.pureComputed((): boolean => {
        const name = this.displayName().trim();
        const url = this.metadataUrl().trim();
        const urlOk = url.length === 0 || (url.length <= 1000 && /^https:\/\/\S+$/i.test(url));
        return name.length > 0 && name.length <= 200 && !!this.preview() && !this.isPreviewing() && urlOk;
    });

    readonly canLeaveAttributes = ko.pureComputed((): boolean => {
        const field = this.primaryField();
        return !!field && IDENTIFIER_FIELDS.some(item => item.id === field) && this.primarySource().trim().length > 0;
    });

    readonly rows = ko.pureComputed((): IProviderRow[] => this.buildRows());

    readonly availableBuiltins = ko.pureComputed((): IAuthenticationType[] => {
        const company = this.sourceCompany();
        return this.catalog()
            .filter(type =>
                type.flag !== AuthenticationTypeFlag.CUSTOM_SAML
                && !this.rows().some(row => row.kind === "builtin" && row.flag === type.flag)
                && (!company || company.authenticationtype?.id !== type.id))
            .sort((a, b) => builtinOrder(a.flag) - builtinOrder(b.flag));
    });

    readonly canChoosePrimary = ko.pureComputed((): boolean => this.editingId() != null && this.editingEnabled);

    readonly builtinNeedsTenant = ko.pureComputed((): boolean => this.builtinFlag() === AuthenticationTypeFlag.AAD_FEDERATION);
    readonly builtinNeedsGoogle = ko.pureComputed((): boolean => this.builtinFlag() === AuthenticationTypeFlag.GOOGLE);
    readonly builtinNeedsGrandId = ko.pureComputed((): boolean => this.builtinFlag() === AuthenticationTypeFlag.GRAND_ID_BANKID_SE);
    readonly builtinTitle = ko.pureComputed((): string => this.catalog().find(type => type.flag === this.builtinFlag())?.name ?? "");
    readonly selectedBuiltin = ko.pureComputed((): IAuthenticationType | undefined =>
        this.catalog().find(type => type.flag === this.selectedBuiltinFlag()));
    readonly isSamlSelected = ko.pureComputed((): boolean => this.samlSelected());
    readonly builtinNeedsCredentials = ko.pureComputed((): boolean => CREDENTIAL_FLAGS.includes(this.builtinFlag()));
    readonly builtinProtocolKey = ko.pureComputed((): string => protocolKey(this.builtinFlag()));

    readonly canLeaveCredentials = ko.pureComputed((): boolean => {
        switch (this.builtinFlag()) {
            case AuthenticationTypeFlag.AAD_FEDERATION:
                return validateObjectGuid(this.builtinTenant().trim());
            case AuthenticationTypeFlag.GOOGLE:
                return this.builtinGoogle().trim().length > 0;
            case AuthenticationTypeFlag.GRAND_ID_BANKID_SE:
                return this.builtinApiKey().trim().length > 0 && this.builtinServiceKey().trim().length > 0;
            default:
                return true;
        }
    });

    readonly primaryRoleHelp = ko.pureComputed((): string => {
        const current = this.sourceCompany()?.authenticationtype;
        const flag = this.builtinStep() ? this.builtinFlag() : AuthenticationTypeFlag.CUSTOM_SAML;
        if (!current || current.flag === flag) {
            return lm.t("sso.idp.role.primary.help") ?? "";
        }
        return lm.t({k: "sso.idp.role.primary.replaces", r: {name: current.name}}, undefined, true) ?? "";
    });

    readonly wizardSteps = ko.pureComputed((): IWizardStep[] => {
        const provider: IWizardStep = {labelKey: "sso.idp.step.protocol", subtitleKey: "sso.idp.step.protocol.subtitle"};
        const flag = this.builtinStep() ? this.builtinFlag() : this.selectedBuiltinFlag();
        if (flag == null) {
            if (this.editingId() != null || this.samlSelected()) {
                return [
                    provider,
                    {labelKey: "sso.idp.step.connection", subtitleKey: "sso.idp.step.connection.subtitle"},
                    {labelKey: "sso.idp.step.attributes", subtitleKey: "sso.idp.step.attributes.subtitle"},
                    {labelKey: "sso.idp.step.summary", subtitleKey: "sso.idp.step.summary.subtitle"},
                ];
            }
            return [provider];
        }
        const review: IWizardStep = {labelKey: "sso.idp.step.review", subtitleKey: "sso.idp.step.review.subtitle"};
        if (!CREDENTIAL_FLAGS.includes(flag)) {
            return [provider, review];
        }
        const name = this.catalog().find(type => type.flag === flag)?.name ?? "";
        return [provider, {labelKey: "sso.idp.step.credentials", subtitleText: name}, review];
    });

    readonly wizardStepIndex = ko.pureComputed((): number => {
        const builtinStep = this.builtinStep();
        if (!builtinStep) {
            return this.step() - 1;
        }
        return builtinStep === "review" ? this.wizardSteps().length - 1 : 1;
    });

    readonly isEmpty = ko.pureComputed((): boolean => !this.isLoading() && this.rows().length === 0);

    constructor(params?: ISsoDetailsParams) {
        this.onAuthenticationSaved = params?.onAuthenticationSaved;
        this.readDraftCompany = params?.readDraftCompany;
        this.writeDraftCompany = params?.writeDraftCompany;
        this.draft = params?.draft === true;
        this.allowCustom = !this.draft;
        this.orgnr = this.draft ? "" : this.readOrgnr();
        this.showList = this.draft || !!this.orgnr;
        if (this.draft && params?.draftRevision) {
            this.draftRevisionSub = params.draftRevision.subscribe(() => {
                this.company(this.readDraftCompany?.() ?? null);
                this.builtinError("");
                this.messageData(null);
                this.mode("list");
                this.resetWizard();
            });
        }
        if (this.showList) {
            this.load(false);
        }
    }

    downloadSp = (): void => {
        this.messageData(null);
        this.loadSpMetadata()
            .then(metadata => {
                const blob = new Blob([metadata.xml], {type: "application/xml"});
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `sp-metadata-${this.orgnr}.xml`;
                link.click();
                URL.revokeObjectURL(link.href);
            })
            .catch((reason: unknown) => this.messageData(createErrorMessage(String(reason))));
    };

    startAdd = (): void => {
        this.resetWizard();
        this.mode("wizard");
    };

    edit = (provider: IIdp): void => {
        this.resetWizard();
        this.editingId(provider.id);
        this.editingEnabled = provider.isenabled;
        this.displayName(provider.displayname ?? "");
        this.metadataUrl(provider.metadataurl ?? "");
        this.metadataSource(provider.metadataurl ? "url" : "file");
        this.wantAssertionSigned(provider.wantassertionsigned);
        this.wantResponseSigned(provider.wantresponsesigned);
        const minutes = Number(provider.allowclockskewminutes ?? 5);
        this.clockSkew(String(Math.min(5, Math.max(0, Number.isFinite(minutes) ? minutes : 0))));
        const mapping = readPrimary(provider.claimmapping);
        this.primaryField(mapping.field || undefined);
        this.primarySource(mapping.source);
        this.preview(storedPreview(provider));
        this.editingProvider(provider);
        this.actorNames({});
        this.loadActors(provider);
        const familyPrimary = this.sourceCompany()?.authenticationtype?.flag === AuthenticationTypeFlag.CUSTOM_SAML;
        this.role(familyPrimary && provider.priority === 1 && provider.isenabled ? "primary" : "secondary");
        this.step(2);
        this.mode("wizard");
        this.ensureSpMetadata();
    };

    cancelWizard = (): void => {
        this.messageData(null);
        this.builtinError("");
        this.mode("list");
        this.resetWizard();
    };

    selectSaml = (): void => {
        this.samlSelected(true);
        this.selectedBuiltinFlag(null);
    };

    startBuiltin = (type: IAuthenticationType): void => {
        this.samlSelected(false);
        this.selectedBuiltinFlag(type.flag);
    };

    builtinLogo = (flag: number): string => {
        switch (flag) {
            case AuthenticationTypeFlag.ID_PORTEN:
                return "/img/logo_idporten.svg";
            case AuthenticationTypeFlag.GOOGLE:
                return "/img/logo_google.svg";
            case AuthenticationTypeFlag.AAD_FEDERATION:
                return "/img/logo_microsoft.png";
            case AuthenticationTypeFlag.GRAND_ID_BANKID_SE:
                return ImgBase64_GrandId;
            case AuthenticationTypeFlag.TELIA_FINLAND:
                return "/img/logo_telia.png";
            case AuthenticationTypeFlag.EADM_NATIVE:
            case AuthenticationTypeFlag.EADM_NATIVE_SMS:
                return "/img/logo_eadm_mark.svg";
            default:
                return "";
        }
    };

    builtinDescriptionKey = (flag: number): string => {
        switch (flag) {
            case AuthenticationTypeFlag.ID_PORTEN:
                return "sso.idp.idporten.hint";
            case AuthenticationTypeFlag.AAD_FEDERATION:
                return "sso.idp.aad.hint";
            case AuthenticationTypeFlag.GOOGLE:
                return "sso.idp.google.hint";
            case AuthenticationTypeFlag.GRAND_ID_BANKID_SE:
                return "sso.idp.grandid.hint";
            case AuthenticationTypeFlag.TELIA_FINLAND:
                return "sso.idp.telia.hint";
            case AuthenticationTypeFlag.EADM_NATIVE:
                return "sso.idp.eadm.hint";
            case AuthenticationTypeFlag.EADM_NATIVE_SMS:
                return "sso.idp.eadmsms.hint";
            default:
                return "";
        }
    };

    editRow = (row: IProviderRow): void => {
        if (row.kind === "builtin" && row.flag != null) {
            this.openBuiltin(row.flag, false);
            return;
        }
        if (row.saml) {
            this.edit(row.saml);
        }
    };

    saveBuiltin = (): void => {
        const company = this.sourceCompany();
        if (!company || this.isSaving()) {
            return;
        }
        const result = applyBuiltinSelection(company, this.catalog(), {
            flag: this.builtinFlag(),
            role: this.builtinRole(),
            tenantId: this.builtinTenant(),
            googleId: this.builtinGoogle(),
            apiKey: this.builtinApiKey(),
            serviceKey: this.builtinServiceKey(),
        });
        if ("error" in result) {
            this.builtinError(lm.t(result.error) ?? "");
            return;
        }
        this.persistCompany(result.company);
    };

    removeBuiltinRow = (row: IProviderRow): void => {
        const company = this.sourceCompany();
        if (!company || row.flag == null || this.isSaving()) {
            return;
        }
        const preview = removeBuiltin(company, row.flag);
        if ("error" in preview) {
            this.messageData({message: lm.t(preview.error) ?? "", type: "danger"});
            return;
        }
        global.dialogManager.addConfirm({
            title: "delete",
            content: `<p>${escapeHTML(lm.t("sso.idp.builtin.remove.confirm") ?? "")}</p>`,
            buttons: {
                cancel: {label: "cancel"},
                confirm: {
                    label: "delete",
                    cls: "btn-destructive",
                    onClick: () => {
                        const current = this.sourceCompany();
                        if (!current || row.flag == null || this.isSaving()) {
                            return;
                        }
                        const result = removeBuiltin(current, row.flag);
                        if ("error" in result) {
                            this.messageData({message: lm.t(result.error) ?? "", type: "danger"});
                            return;
                        }
                        this.persistCompany(result.company);
                    },
                },
            },
        });
    };

    next = (): void => {
        if (this.step() === 1) {
            if (this.selectedBuiltin()) {
                this.openBuiltin(this.selectedBuiltin()!.flag, true);
                return;
            }
            if (!this.samlSelected()) {
                return;
            }
        }
        if (this.step() === 2 && !this.canLeaveConnection()) {
            return;
        }
        if (this.step() === 3 && !this.canLeaveAttributes()) {
            return;
        }
        const enteringConnection = this.step() === 1;
        this.step(Math.min(4, this.step() + 1));
        if (enteringConnection) {
            this.ensureSpMetadata();
        }
    };

    back = (): void => {
        this.step(Math.max(this.editingId() == null ? 1 : 2, this.step() - 1));
    };

    nextBuiltin = (): void => {
        if (this.canLeaveCredentials()) {
            this.builtinStep("review");
        }
    };

    backBuiltin = (): void => {
        this.builtinError("");
        if (this.builtinStep() === "review" && this.builtinNeedsCredentials()) {
            this.builtinStep("credentials");
            return;
        }
        this.builtinStep(null);
        this.step(1);
    };

    readonly canGoBackBuiltin = ko.pureComputed((): boolean =>
        this.builtinIsNew() || (this.builtinStep() === "review" && this.builtinNeedsCredentials()));

    useFile = (): void => {
        if (this.metadataSource() === "file") {
            return;
        }
        this.metadataSource("file");
        this.metadataUrl("");
        this.preview(null);
        this.previewError("");
    };

    useUrl = (): void => {
        if (this.metadataSource() === "url") {
            return;
        }
        this.metadataSource("url");
        this.uploadedXml("");
        this.uploadedFileName("");
        this.isDragging(false);
        this.preview(null);
        this.previewError("");
    };

    onDragOver = (_view: unknown, event: Event): void => {
        const drag = nativeDrag(event);
        if (drag.dataTransfer) {
            drag.dataTransfer.dropEffect = "copy";
        }
        this.isDragging(true);
    };

    onDragLeave = (_view: unknown, event: Event): void => {
        const drag = nativeDrag(event);
        const zone = event.currentTarget as HTMLElement;
        const next = drag.relatedTarget as Node | null;
        if (next && zone.contains(next)) {
            return;
        }
        this.isDragging(false);
    };

    onFile = (_view: unknown, event: Event): void => {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = "";
        this.readMetadataFile(file);
    };

    onDrop = (_view: unknown, event: Event): void => {
        this.isDragging(false);
        this.readMetadataFile(nativeDrag(event).dataTransfer?.files?.[0]);
    };

    readFromUrl = (): void => {
        if (this.isPreviewing() || !this.metadataUrl().trim()) {
            return;
        }
        this.uploadedXml("");
        this.uploadedFileName("");
        this.requestPreview("", this.metadataUrl().trim());
    };

    replaceMetadata = (): void => {
        this.uploadedXml("");
        this.uploadedFileName("");
        this.preview(null);
        this.previewError("");
        this.isDragging(false);
    };

    readonly previewEntityId = ko.pureComputed(() => this.preview()?.entityid ?? "");
    readonly previewSsoUrl = ko.pureComputed(() => this.preview()?.ssourl ?? "");
    readonly previewCertificates = ko.pureComputed(() => this.preview()?.certificates ?? []);
    readonly parsedSourceUrl = ko.pureComputed(() => this.metadataSource() === "url" ? this.metadataUrl() : "");
    readonly refreshLast = ko.pureComputed((): string => this.providerRefreshLast(this.editingProvider()));
    readonly refreshNext = ko.pureComputed((): string => this.providerRefreshNext(this.editingProvider()));
    readonly refreshFailing = ko.pureComputed((): boolean => this.providerRefreshFailing(this.editingProvider()));

    providerSystemCreated = (provider: IIdp | undefined): string => {
        if (!provider) {
            return "";
        }
        return this.systemLine(
            provider.createddate,
            provider.createdby,
            "sso.companyidp.system.created",
            "sso.companyidp.system.createdby",
            "Created",
        );
    };

    providerSystemModified = (provider: IIdp | undefined): string => {
        if (!provider) {
            return "";
        }
        return this.systemLine(
            provider.modifieddate,
            provider.modifiedby,
            "sso.companyidp.system.modified",
            "sso.companyidp.system.modifiedby",
            "Last modified",
        );
    };

    save = (): void => {
        if (this.isSaving() || !this.canLeaveConnection() || !this.canLeaveAttributes()) {
            return;
        }
        this.isSaving(true);
        this.messageData(null);
        const minutes = Number(this.clockSkew());
        const payload = {
            orgnr: this.orgnr,
            displayname: this.displayName().trim(),
            metadataxml: this.metadataSource() === "file" ? this.uploadedXml() : "",
            metadataurl: this.metadataUrl().trim(),
            claimmapping: JSON.stringify({
                mappings: [{source: this.primarySource().trim(), target: this.primaryField()}],
            }),
            wantassertionsigned: this.wantAssertionSigned(),
            wantresponsesigned: this.wantResponseSigned(),
            allowclockskewminutes: Math.min(5, Math.max(0, Number.isFinite(minutes) ? minutes : 0)),
            isenabled: this.editingId() == null ? false : this.editingEnabled,
        };
        if (this.editingId() != null && this.editingEnabled) {
            (payload as { primary?: boolean }).primary = this.role() === "primary";
        }
        const request = this.editingId() == null
            ? this.reqCreate.request({...payload, protocol: IdentityProviderProtocol.SAML})
            : this.reqUpdate.request({...payload, id: this.editingId()});
        request
            .then((saved: IIdp) => {
                const warnings = saved?.warnings ?? [];
                this.messageData(warnings.length
                    ? {message: warnings.join(" "), type: "warning"}
                    : {message: lm.t("sso.idp.saved") ?? "", type: "success"});
                this.mode("list");
                this.resetWizard();
                this.reload(true);
            })
            .catch((reason: unknown) => this.messageData(createErrorMessage(String(reason))))
            .finally(() => this.isSaving(false));
    };

    menuFor = (provider: IIdp): IDropdownButton[] => [
        {
            label: provider.isenabled ? "sso.idp.action.disable" : "sso.idp.action.enable",
            icon: provider.isenabled ? "fa-ban" : "fa-power-off sso-enable-icon",
            onClick: () => this.setEnabled(provider, !provider.isenabled),
        },
        {
            cls: "btn-separated btn-destructive explicit",
            label: "delete",
            icon: "fa-trash-alt",
            onClick: () => this.remove(provider),
        },
    ];

    canExpand = (row: IProviderRow): boolean =>
        row.kind === "saml" || (row.kind === "builtin" && row.flag != null && CREDENTIAL_FLAGS.includes(row.flag));

    isExpanded = (row: IProviderRow | undefined): boolean =>
        !!row && this.expandedRowKey() === row.key;

    toggleExpanded = (row: IProviderRow): void => {
        if (!this.canExpand(row)) {
            return;
        }
        if (this.expandedRowKey() === row.key) {
            this.expandedRowKey(null);
            return;
        }
        if (row.kind === "saml" && row.saml) {
            this.detailTab("connection");
            this.loadActors(row.saml);
        }
        this.expandedRowKey(row.key);
    };

    builtinCredentialRows = (flag: number | undefined): { labelKey: string, value: string, secret: boolean }[] => {
        const company = this.sourceCompany();
        if (!company || flag == null) {
            return [];
        }
        switch (flag) {
            case AuthenticationTypeFlag.AAD_FEDERATION:
                return [{labelKey: "SSO tenant id", value: company.ssotenantid?.trim() || "—", secret: false}];
            case AuthenticationTypeFlag.GOOGLE:
                return [{labelKey: "Google IDP id", value: company.ssogoogleidpid?.trim() || "—", secret: false}];
            case AuthenticationTypeFlag.GRAND_ID_BANKID_SE:
                return [
                    {labelKey: "API key", value: company.grandidapikey?.trim() ? "••••••••" : "—", secret: true},
                    {labelKey: "Service key", value: company.grandidservicekey?.trim() ? "••••••••" : "—", secret: true},
                ];
            default:
                return [];
        }
    };

    providerRefreshLast = (provider: IIdp | null | undefined): string =>
        auditStamp(provider?.metadataurl?.trim() ? provider.metadatalastrefreshed : "");

    providerRefreshNext = (provider: IIdp | null | undefined): string => {
        if (!provider?.metadataurl?.trim()) {
            return "";
        }
        const date = auditStamp(provider.metadatanextrefreshat);
        if (!date) {
            return "";
        }
        if ((provider.metadatarefreshfailurecount ?? 0) > 0) {
            return lm.t({k: "sso.companyidp.refresh.retrying", r: {date}}, undefined, true) ?? `Retrying at ${date}`;
        }
        return date;
    };

    providerRefreshFailing = (provider: IIdp | null | undefined): boolean =>
        !!provider?.metadataurl?.trim() && (provider.metadatarefreshfailurecount ?? 0) > 0;

    mappingSource = (provider: IIdp): string => readPrimary(provider.claimmapping ?? "").source;

    mappingFieldKey = (provider: IIdp): string =>
        IDENTIFIER_FIELDS.find(item => item.id === readPrimary(provider.claimmapping ?? "").field)?.labelKey ?? "";

    expiryLabel = (provider: IIdp): string => serverDate(primaryCert(provider.certificates)?.notafter);

    expiryDays = (provider: IIdp): number | null => {
        const value = primaryCert(provider.certificates)?.notafter;
        if (!value || !validateServerDate(value)) {
            return null;
        }
        return Math.ceil((convertFromServerString(value).valueOf() - Date.now()) / 86400000);
    };

    expirySoon = (provider: IIdp): boolean => {
        const days = this.expiryDays(provider);
        return days != null && days >= 0 && days <= 30;
    };

    certificateSummary = (): string => {
        const cert = primaryCert(this.preview()?.certificates);
        if (!cert?.notafter) {
            return "";
        }
        const formatted = serverDate(cert.notafter);
        return lm.t({k: "sso.companyidp.summary.certificate.parsed", r: {date: formatted}}, undefined, true)
            ?? `Parsed from metadata · expires ${formatted}`;
    };

    dispose(): void {
        this.draftRevisionSub?.dispose();
        this.spMetadataGeneration++;
        this.spMetadataLoad = null;
        this.spMetadataResult = null;
        this.testLoginSession.finish(null);
        this.reqList.abort();
        this.reqPreview.abort();
        this.reqCreate.abort();
        this.reqUpdate.abort();
        this.reqDelete.abort();
        this.reqTest.abort();
        this.reqCompany.abort();
        this.reqTypes.abort();
        this.reqUpdateCompany.abort();
    }

    private ensureSpMetadata(): void {
        if (this.draft || !this.allowCustom) {
            return;
        }
        this.loadSpMetadata().catch(() => undefined);
    }

    private loadSpMetadata(): Promise<{ xml: string, rollover: boolean }> {
        const cached = this.spMetadataResult;
        if (cached && cached.generation === this.spMetadataGeneration) {
            return Promise.resolve(cached.metadata);
        }
        const current = this.spMetadataLoad;
        if (current && current.generation === this.spMetadataGeneration) {
            return current.request;
        }

        const generation = this.spMetadataGeneration;
        const url = this.spMetadataUrl();
        if (!url) {
            return Promise.reject(new Error("Metadata URL is missing."));
        }

        this.spMetadataLoading(true);
        this.spRollover(false);
        const request = fetch(url, {cache: "no-store"})
            .then(response => {
                if (!response.ok) {
                    throw new Error(String(response.status));
                }
                const rollover = response.headers.get("x-eadm-sp-certificate-rollover") === "true";
                return response.text().then(xml => ({xml, rollover}));
            })
            .then(metadata => {
                if (generation === this.spMetadataGeneration) {
                    this.spRollover(metadata.rollover);
                    this.spMetadataResult = {generation, metadata};
                }
                return metadata;
            })
            .catch((reason: unknown) => {
                if (generation === this.spMetadataGeneration) {
                    this.spRollover(false);
                }
                throw reason;
            })
            .finally(() => {
                if (generation === this.spMetadataGeneration) {
                    this.spMetadataLoading(false);
                    this.spMetadataLoad = null;
                }
            });
        this.spMetadataLoad = {generation, request};
        return request;
    }

    private async load(notify: boolean): Promise<void> {
        this.isLoading(true);
        try {
            if (this.draft) {
                this.company(this.readDraftCompany?.() ?? null);
                const types: IAuthenticationType | IAuthenticationType[] = await this.reqTypes.request();
                this.catalog(asArray(types));
                return;
            }
            const result: [ICompany | ICompany[], IAuthenticationType | IAuthenticationType[], IIdp | IIdp[]] = await Promise.all([
                this.reqCompany.request({orgnr: this.orgnr}),
                this.reqTypes.request(),
                this.reqList.request({orgnr: this.orgnr}),
            ]);
            const company = asArray(result[0])[0] ?? null;
            this.company(company);
            this.catalog(asArray(result[1]));
            this.providers(asArray(result[2]));
            if (this.providers().length > 0) {
                this.ensureSpMetadata();
            }
            if (notify && company) {
                this.onAuthenticationSaved?.(company);
            }
        } catch (reason: unknown) {
            this.messageData(createErrorMessage(String(reason)));
        } finally {
            this.isLoading(false);
        }
    }

    private reload(notify: boolean): Promise<void> {
        global.cacheManager.removeCache("getcompany");
        global.cacheManager.removeCache("getcompanyidps");
        global.cacheManager.removeCache("getcompanylogin");
        return this.load(notify);
    }

    /** Draft create uses the parent form as source of truth; local company() can go stale after discard. */
    private sourceCompany(): ICompany | null {
        if (this.draft) {
            return this.readDraftCompany?.() ?? null;
        }
        return this.company();
    }

    private persistCompany(next: ICompany): void {
        if (this.draft) {
            this.writeDraftCompany?.(next);
            this.company(this.readDraftCompany?.() ?? next);
            this.builtinError("");
            this.mode("list");
            this.resetWizard();
            return;
        }
        // updatecompany always overwrites ServiceUserName/Password — refuse empty
        // credentials (getcompany blanks them for non–org-admins) instead of wiping live values.
        const username = (next.eadmapiusername ?? "").trim();
        const password = (next.eadmapipassword ?? "").trim();
        if (!username || !password) {
            const message = lm.t("sso.idp.builtin.apicredentials") ?? "";
            this.builtinError(message);
            this.messageData(createErrorMessage(message));
            return;
        }
        const payload = {
            ...next,
            syncsteps: urlEncodeCustomerKeys(next.syncsteps ?? []),
            eadmapiusername: encodeURIComponent(username),
            eadmapipassword: encodeURIComponent(password),
        };
        this.isSaving(true);
        this.messageData(null);
        this.reqUpdateCompany.request({orgnr: this.orgnr, jcompany: JSON.stringify(payload)})
            .then(() => {
                this.builtinError("");
                this.mode("list");
                this.messageData({message: lm.t("sso.idp.saved") ?? "", type: "success"});
                return this.reload(true);
            })
            .catch((reason: unknown) => this.messageData(createErrorMessage(String(reason))))
            .finally(() => this.isSaving(false));
    }

    private openBuiltin(flag: number, isNew: boolean): void {
        const company = this.sourceCompany();
        this.builtinIsNew(isNew);
        this.builtinFlag(flag);
        this.builtinError("");
        if (isNew || !company) {
            this.builtinRole("secondary");
            this.builtinTenant("");
            this.builtinGoogle("");
            this.builtinApiKey("");
            this.builtinServiceKey("");
        } else {
            this.builtinRole(company.authenticationtype?.flag === flag ? "primary" : "secondary");
            this.builtinTenant(company.ssotenantid ?? "");
            this.builtinGoogle(company.ssogoogleidpid ?? "");
            this.builtinApiKey(company.grandidapikey ?? "");
            this.builtinServiceKey(company.grandidservicekey ?? "");
        }
        this.builtinStep(CREDENTIAL_FLAGS.includes(flag) ? "credentials" : "review");
        this.mode("wizard");
    }

    private buildRows(): IProviderRow[] {
        const company = this.sourceCompany();
        const catalog = this.catalog();
        const enabled = catalog.filter(type =>
            type.flag !== AuthenticationTypeFlag.CUSTOM_SAML
            && !!company
            && (isBit(type.flag, company.authenticationtypes) || company.authenticationtype?.id === type.id));
        const builtinRows = enabled.map((type): IProviderRow => ({
            key: `builtin-${type.flag}`,
            kind: "builtin",
            name: type.name,
            subtitle: "",
            protocolKey: protocolKey(type.flag),
            active: true,
            roleKey: company?.authenticationtype?.id === type.id ? "Primary" : "Secondary",
            statusClass: "sso-badge--active",
            flag: type.flag,
        }));
        const familyPrimary = company?.authenticationtype?.flag === AuthenticationTypeFlag.CUSTOM_SAML;
        const customRows = this.providers().map((provider): IProviderRow => ({
            key: `saml-${provider.id}`,
            kind: "saml",
            name: provider.displayname,
            subtitle: provider.entityid ?? "",
            protocolKey: authProtocolKey(provider.protocol),
            active: provider.isenabled,
            roleKey: familyPrimary && provider.priority === 1 ? "Primary" : "Secondary",
            statusClass: provider.isenabled ? "sso-badge--active" : "sso-badge--inactive",
            saml: provider,
        }));
        if (familyPrimary) {
            return customRows.concat(builtinRows);
        }
        const primary = builtinRows.filter(row => catalog.find(type => type.flag === row.flag)?.id === company?.authenticationtype?.id);
        const rest = builtinRows.filter(row => catalog.find(type => type.flag === row.flag)?.id !== company?.authenticationtype?.id);
        return primary.concat(rest, customRows);
    }

    private readMetadataFile(file: File | undefined): void {
        if (!file) {
            return;
        }
        if (file.size > 256 * 1024) {
            this.previewError(lm.t("sso.companyidp.file.toobig") ?? "");
            return;
        }
        this.uploadedFileName(file.name);
        const reader = new FileReader();
        reader.onload = () => {
            this.uploadedXml(String(reader.result ?? ""));
            this.requestPreview(this.uploadedXml(), "");
        };
        reader.readAsText(file);
    }

    private requestPreview(xml: string, url: string): void {
        this.isPreviewing(true);
        this.previewError("");
        this.reqPreview.request({orgnr: this.orgnr, protocol: IdentityProviderProtocol.SAML, metadataxml: xml, metadataurl: url})
            .then((data: IMetadataPreview | IMetadataPreview[]) => {
                this.preview(Array.isArray(data) ? data[0] ?? null : data);
                this.previewError("");
            })
            .catch((reason: unknown) => {
                this.uploadedXml("");
                this.uploadedFileName("");
                this.preview(null);
                this.previewError(String(reason));
            })
            .finally(() => this.isPreviewing(false));
    }

    testLogin(provider: IIdp): void {
        const popup = this.testLoginSession.start();
        if (popup === "busy") {
            return;
        }
        if (popup === "blocked") {
            this.showTestResult("sso.idp.test.blocked");
            return;
        }

        this.testingIdpId(provider.id);
        this.messageData(null);
        this.reqTest.request({orgnr: this.orgnr, id: provider.id})
            .then((url: string) => this.testLoginSession.navigate(popup, url))
            .catch((reason: unknown) => {
                this.testLoginSession.finish(null);
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private showTestResult(key: string): void {
        global.snackbarData({
            message: lm.t(key) ?? "",
            type: key === "sso.idp.test.success" ? "success" : "danger",
        });
    }

    private setEnabled(provider: IIdp, enabled: boolean): void {
        if (!enabled && this.isMarkedPrimarySaml(provider)) {
            this.messageData({message: lm.t("sso.idp.builtin.keepprimary") ?? "", type: "danger"});
            return;
        }
        this.messageData(null);
        this.reqUpdate.request({
            orgnr: this.orgnr,
            id: provider.id,
            displayname: provider.displayname,
            metadataurl: provider.metadataurl ?? "",
            claimmapping: provider.claimmapping ?? "",
            wantassertionsigned: provider.wantassertionsigned,
            wantresponsesigned: provider.wantresponsesigned,
            allowclockskewminutes: provider.allowclockskewminutes,
            isenabled: enabled,
        })
            .then(() => this.reload(true))
            .catch((reason: unknown) => this.messageData(createErrorMessage(String(reason))));
    }

    private remove(provider: IIdp): void {
        if (this.isMarkedPrimarySaml(provider)) {
            this.messageData({message: lm.t("sso.idp.builtin.keepprimary") ?? "", type: "danger"});
            return;
        }
        global.dialogManager.addConfirm({
            title: "delete",
            content: `<p>${escapeHTML(lm.t("sso.idp.delete.confirm") ?? "")}</p>`,
            buttons: {
                cancel: {label: "cancel"},
                confirm: {
                    label: "delete",
                    cls: "btn-destructive",
                    onClick: () => {
                        this.messageData(null);
                        this.reqDelete.request({orgnr: this.orgnr, id: provider.id})
                            .then(() => this.reload(true))
                            .catch((reason: unknown) => this.messageData(createErrorMessage(String(reason))));
                    },
                },
            },
        });
    }

    private isMarkedPrimarySaml(provider: IIdp): boolean {
        return this.sourceCompany()?.authenticationtype?.flag === AuthenticationTypeFlag.CUSTOM_SAML
            && provider.priority === 1;
    }

    private resetWizard(): void {
        this.step(1);
        this.selectedBuiltinFlag(null);
        this.samlSelected(false);
        this.builtinIsNew(false);
        this.builtinStep(null);
        this.editingId(null);
        this.editingProvider(null);
        this.actorNames({});
        this.editingEnabled = false;
        this.role("secondary");
        this.displayName("");
        this.metadataUrl("");
        this.metadataSource("url");
        this.uploadedXml("");
        this.uploadedFileName("");
        this.preview(null);
        this.previewError("");
        this.primaryField(undefined);
        this.primarySource("");
        this.wantAssertionSigned(true);
        this.wantResponseSigned(false);
        this.clockSkew("5");
    }

    private readOrgnr(): string {
        const route = global.router.currentRoute();
        if (route?.__routeDef?.id === "createcustomer") {
            return "";
        }
        const fromRoute = route?.__routeParams?.["orgnr"];
        if (fromRoute) {
            return String(fromRoute);
        }
        return global.login.selectedOrganizationId() ?? "";
    }

    private loadActors(provider: IIdp): void {
        const ids = [provider.createdby, provider.modifiedby]
            .filter((id): id is number => !!id);
        const pending = [...new Set(ids)];
        const me = global.login.user();
        const myName = me?.id ? personName(me) : "";
        if (me?.id && myName) {
            this.actorNames({[me.id]: myName});
        }
        pending.filter(id => !this.actorNames()[id]).forEach(id => {
            new PromiseRequest("getobject").request({id, manual: false, orgnr: this.orgnr})
                .then((data: IObjectData | IObjectData[]) => {
                    const person = Array.isArray(data) ? data[0] : data;
                    const label = personName(person);
                    const stillRelevant = this.editingProvider()?.id === provider.id
                        || this.expandedRowKey() === `saml-${provider.id}`;
                    if (!label || !stillRelevant) {
                        return;
                    }
                    this.actorNames({...this.actorNames(), [id]: label});
                })
                .catch(() => undefined);
        });
    }

    private systemLine(rawDate: string | null | undefined, userId: number | null | undefined, dateKey: string, byKey: string, fallback: string): string {
        const date = auditStamp(rawDate);
        if (!date) {
            return "";
        }
        const by = this.actorLink(userId);
        if (by) {
            return lm.t({k: byKey, r: {date, by}}, undefined, true) ?? `${fallback}: ${date} by ${by}`;
        }
        return lm.t({k: dateKey, r: {date}}, undefined, true) ?? `${fallback}: ${date}`;
    }

    private actorLink(userId: number | null | undefined): string {
        const name = userId ? this.actorNames()[userId] : "";
        if (!name) {
            return "";
        }
        const safeName = escapeHTML(name);
        const href = getHash("userdetails", {id: userId}, true, this.orgnr);
        return href ? `<a href="${escapeHTML(href)}">${safeName}</a>` : safeName;
    }
}

function builtinOrder(flag: number): number {
    const index = BUILTIN_ORDER.indexOf(flag);
    return index === -1 ? BUILTIN_ORDER.length : index;
}

function nativeDrag(event: Event): DragEvent {
    const wrapped = event as DragEvent & { originalEvent?: DragEvent };
    return wrapped.originalEvent ?? wrapped;
}

function asArray<T>(data: T | T[] | null | undefined): T[] {
    if (Array.isArray(data)) {
        return data;
    }
    return data ? [data] : [];
}

function primaryCert(certificates: IIdpCertificate[] | undefined): IIdpCertificate | undefined {
    return (certificates ?? []).find(certificate => certificate.isprimary) ?? certificates?.[0];
}

function serverDate(value: string | null | undefined): string {
    if (!value?.trim()) {
        return "";
    }
    return validateServerDate(value) ? getDate(value, true) : value;
}

function auditStamp(value: string | null | undefined): string {
    if (!value?.trim()) {
        return "";
    }
    return validateServerDate(value) ? convertFromServerString(value).format("YYYY-MM-DD HH:mm") : value.trim();
}

function personName(person: IObjectData | null | undefined): string {
    if (!person?.syncfields) {
        return "";
    }
    return getSyncFieldDisplayName(ObjectTypeId.User, person.syncfields).trim();
}

function storedPreview(provider: IIdp): IMetadataPreview {
    return {
        entityid: provider.entityid,
        ssourl: provider.ssourl,
        slourl: provider.slourl,
        nameidformat: provider.nameidformat,
        metadatavaliduntil: "",
        warnings: [],
        certificates: provider.certificates ?? [],
        attributes: provider.attributes ?? [],
    };
}

function readPrimary(json: string): { field: string, source: string } {
    try {
        const document = JSON.parse(json) as { mappings?: { source?: string, target?: string }[] };
        const mapping = document?.mappings?.[0];
        if (mapping?.source && mapping?.target && IDENTIFIER_FIELDS.some(field => field.id.toLowerCase() === mapping.target!.toLowerCase())) {
            const field = IDENTIFIER_FIELDS.find(item => item.id.toLowerCase() === mapping.target!.toLowerCase());
            return {field: field?.id ?? "", source: mapping.source};
        }
    } catch {
        // Stored mapping is shown empty when it is not the single identifier shape.
    }
    return {field: "", source: ""};
}
